import { useEffect, useRef, useState } from 'react';
import api from '../api/endpoints';
import { Modal } from './Ui';
import { Reveal, stagger } from './Motion';
import { colors } from '../theme';

/**
 * Searchable city field backed by Google Places.
 *
 * Selecting a city also resolves its district and state, so the user types one
 * thing and we store all three. Free text is deliberately not accepted - a
 * typed "delhi ncr" cannot be matched to a district, and inconsistent spellings
 * make city filtering useless later.
 *
 * The lookup endpoint is public, so this works on the signup form before the
 * user has an account.
 */
const CityPicker = ({
  label = 'City', value, onChange, placeholder = 'Search your city', error,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef(null);

  // Debounced so a fast typist costs one Places call, not eight.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return undefined; }

    setLoading(true);
    const timer = setTimeout(() => {
      api.maps.cities(q)
        .then((res) => { setResults(res.data || []); setFailed(false); })
        .catch(() => { setResults([]); setFailed(true); })
        .finally(() => setLoading(false));
    }, 320);
    return () => clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const choose = async (item) => {
    setResolving(item.placeId);
    try {
      const res = await api.maps.cityDetails(item.placeId);
      const { city, district, state, coordinates } = res.data || {};
      // Coordinates ride along so a form can place the business without
      // falling back to wherever the app happens to think "home" is.
      onChange({ city: city || item.city, district: district || '', state: state || '', coordinates: coordinates || null });
    } catch (err) {
      // Still let them proceed with the city name Google already gave us.
      onChange({ city: item.city, district: '', state: '' });
    } finally {
      setResolving(null);
      setOpen(false);
      setQuery('');
    }
  };

  const summary = value && value.city
    ? [
      value.city,
      value.district && value.district !== value.city ? value.district : null,
      value.state,
    ].filter(Boolean).join(' · ')
    : '';

  return (
    <div className="field">
      {label ? <label className="field-label">{label}</label> : null}

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="press"
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
          height: 50, padding: '0 12px', borderRadius: 12, background: '#fff',
          border: `1.2px solid ${error ? colors.danger : colors.line}`,
        }}
      >
        <span style={{ fontSize: 15 }}>📍</span>
        <span
          className="t-body grow clamp-1"
          style={{ fontWeight: 600, color: summary ? colors.ink : colors.faint }}
        >
          {summary || placeholder}
        </span>
        <span style={{ fontSize: 20, color: colors.faint }}>›</span>
      </button>

      {error ? <div className="field-error">{error}</div> : null}

      <Modal open={open} onClose={() => setOpen(false)} title="Choose your city">
        <div className="input-wrap" style={{ background: colors.bg, border: 0 }}>
          <span>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Start typing, e.g. Karnal"
            autoComplete="off"
            aria-label="Search for a city"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              style={{
                border: 0, width: 22, height: 22, borderRadius: 11,
                background: colors.line, color: colors.muted, fontSize: 12,
              }}
            >
              ✕
            </button>
          ) : null}
        </div>

        <div style={{ marginTop: 14, maxHeight: '52vh', overflowY: 'auto' }}>
          {results.length ? results.map((item, i) => (
            <Reveal key={item.placeId} delay={stagger(i, 30, 200)}>
              <button
                type="button"
                onClick={() => choose(item)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                  padding: '12px 4px', background: 'none', border: 0,
                  borderBottom: `1px solid ${colors.line}`,
                }}
              >
                <span style={{ fontSize: 17 }}>🏙️</span>
                <span className="grow">
                  <span className="t-body" style={{ display: 'block', fontWeight: 700, color: colors.ink }}>
                    {item.city}
                  </span>
                  {item.region ? <span className="t-tiny">{item.region}</span> : null}
                </span>
                {resolving === item.placeId ? (
                  <span className="spinner" style={{ color: colors.primary }} />
                ) : null}
              </button>
            </Reveal>
          )) : (
            <div style={{ textAlign: 'center', padding: '32px 20px' }}>
              {loading ? (
                <span className="spinner lg" style={{ color: colors.primary }} />
              ) : (
                <>
                  <div style={{ fontSize: 34 }}>{failed ? '📡' : '🔎'}</div>
                  <p className="t-small" style={{ marginTop: 12, lineHeight: 1.6 }}>
                    {failed
                      ? 'Could not reach city search. Check your connection and try again.'
                      : query.trim().length < 2
                        ? 'Type at least 2 letters to search'
                        : 'No cities matched that search'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CityPicker;
